import { EventModel, Toast } from '@bryntum/scheduler';
import { BryntumScheduler, BryntumSchedulerProps, BryntumSplitter } from '@bryntum/scheduler-react';
import { endOfDay, format, startOfDay } from 'date-fns';
import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { WebhookConnector } from '../../../../Components/Webhook';
import { DispatchDto, HaulerManagementViewDto, OrderByType, OrderLineItemDto } from '../../../../dtos';
import {
    useArchiveDispatchDeleteFromDatabaseByIdMutation,
    useLazyGetDispatchHaulerQuery,
    useUpdateDispatchMutation,
} from '../../../../store/generated/generatedApi';
import { areDateTimesEqual, emptyGuid, LineItemTypes, toEasternDateKey } from '../../../../util';
import { getDispatchSaveDates } from '../../DispatchesHelper';
import { EventEditor } from '../../Components';
import DispatchEventModel from '../../Stores/DispatchEvent/DispatchEventModel';
import HaulerDispatchStore from '../../Stores/DispatchEvent/DispatchEventStore';
import { OrderLineItemGrid } from '../Grids/OrderLineItemGrid';
import { HaulerManagementDispatchingViewScheduler } from './HaulerManagementDispatchingViewScheduler';
import { calculateInitialHaulQuantityForNewDispatch } from '../../../../Components/Forms/OrderReviewForm/useOrderReviewForm';

export interface IHaulerManagementDispatchingViewProps {
    date: Date;
    isEditEventOpen: boolean;
    schedulerConfig: BryntumSchedulerProps;
    schedulerRef: MutableRefObject<BryntumScheduler>;
    selectedEvent?: EventModel;
    setEditEventOpen: (isOpen: boolean) => void;
    setSelectedEvent: (event?: EventModel) => void;
    disabled?: boolean;
    setIsLoading: (isLoading: boolean) => void;
    webhookConnector: WebhookConnector;
    setCounts: (counts: {
        total: number;
        completed: number;
        released: number;
        needsReleased: number;
    }) => void;
}

export const HaulerManagementDispatchingView: FC<IHaulerManagementDispatchingViewProps> = (props) => {
    const {
        date: currentDate,
        isEditEventOpen,
        schedulerConfig,
        schedulerRef,
        selectedEvent,
        setEditEventOpen,
        setSelectedEvent,
        disabled = false,
        setIsLoading,
        webhookConnector,
        setCounts,
    } = props;
    const [updateDispatch] = useUpdateDispatchMutation();
    const pendingSaveCount = useRef(0);
    const [deleteDispatch] = useArchiveDispatchDeleteFromDatabaseByIdMutation();
    const [getData, { data: haulerManagementData, isFetching: loadingData }] = useLazyGetDispatchHaulerQuery();
    const [date, setDate] = useState<Date>(currentDate);
    const [orderLineItems, setOrderLineItems] = useState<OrderLineItemDto[]>([]);
    const [dispatches, setDispatches] = useState<DispatchDto[]>([]);

    const [crudManager] = useState({
        eventStore: {
            // @ts-ignore
            storeClass: HaulerDispatchStore,
            autoLoad: true,
            syncDataOnLoad: true,
        },
        assignmentStore: {
            autoLoad: true,
            syncDataOnLoad: true,
        },
        resourceStore: {
            autoLoad: true,
            syncDataOnLoad: true,
        },
    } as any);

    const loadData = useCallback(
        (dateToLoad: Date) => {
            if (format(dateToLoad, 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd')) {
                Promise.all([getData({ startDate: format(dateToLoad, 'yyyy-MM-dd') })]).then((data) => {
                    const haulerManagementViewDto = data[0].data as HaulerManagementViewDto;
                    setCounts({
                        total: haulerManagementViewDto.totalCount,
                        completed: haulerManagementViewDto.completedCount,
                        released: haulerManagementViewDto.releasedCount,
                        needsReleased: haulerManagementViewDto.needsReleaseCount,
                    });
                });
            }
        },
        [currentDate, getData, setCounts]
    );

    useEffect(() => {
        if (date !== currentDate) {
            loadData(currentDate);
            setDate(currentDate);
        }
    }, [currentDate, date, loadData, webhookConnector]);

    useEffect(() => {
        loadData(currentDate);
        // Scroll the timeline to 6am
        const startDate = startOfDay(date);
        const scrollToTime = new Date(startDate);
        scrollToTime.setHours(6);
        schedulerRef?.current?.instance.scrollToDate(scrollToTime, { block: 'start' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const startDate = startOfDay(date);
        const endDate = endOfDay(startDate);
        schedulerRef?.current?.instance?.setTimeSpan(startDate, endDate);
    }, [haulerManagementData, date, schedulerRef, schedulerRef?.current?.instance]);

    useEffect(() => {
        setOrderLineItems(haulerManagementData?.orderLineItems ?? []);
    }, [haulerManagementData?.orderLineItems]);

    useEffect(() => {
        setDispatches(haulerManagementData?.events ?? []);
    }, [haulerManagementData?.events]);

    const calculateHourlyHaulQuantity = useCallback((data: DispatchDto) => {
        const hours = (new Date(data.endDate).getTime() - new Date(data.startDate).getTime()) / (1000 * 60 * 60);
        return hours;
    }, []);

    useEffect(() => {
        if (!webhookConnector) {
            return;
        }
        const { events } = webhookConnector!;
        if (!events) {
            return;
        }
        // Unsubscribe on re-run/unmount so a handler holding an old board date can't reload the wrong day
        return events((updatedDate, id) => {
            // The server sends the changed record's instant; reload when it falls on the board's Eastern day.
            if (toEasternDateKey(updatedDate) === format(date, 'yyyy-MM-dd')) {
                loadData(date);
                if (id === selectedEvent?.id) {
                    setEditEventOpen(false);
                }
            }
        });
    }, [loadData, setEditEventOpen, selectedEvent?.id, date, schedulerRef?.current?.instance, webhookConnector]);

    // 5-minute refresh timer for hauler availability updates
    useEffect(() => {
        const intervalId = setInterval(() => {
            loadData(date);
        }, 5 * 60 * 1000); // 5 minutes

        return () => {
            clearInterval(intervalId);
        };
    }, [loadData, date]);

    useEffect(() => {
        setIsLoading(loadingData);
    }, [loadingData, setIsLoading]);

    const createDispatchDto = useCallback(
        (data: any) => {
            // Keep the event within the board's day (wall dates) and get the instants to save
            const saveDates = getDispatchSaveDates(data.startDate, data.endDate, date);
            data.startDate = saveDates.wallStart;
            data.endDate = saveDates.wallEnd;

            // scroll to the start date of the new event
            schedulerRef.current.instance.scrollToDate(data.startDate);

            let haulQuantity = data.haulQuantity;
            const lineItem = orderLineItems.find((r: any) => r.id === data.orderLineItemId);
            if (lineItem && lineItem.lineItemType === LineItemTypes.Hourly && lineItem.orderBy === OrderByType.Quantity) {
                haulQuantity = calculateHourlyHaulQuantity(data);
            }
            else if (haulQuantity === undefined) {
                const orderLineItem = orderLineItems.find((r: any) => r.id === data.orderLineItemId);
                if (orderLineItem) {
                    haulQuantity = calculateInitialHaulQuantityForNewDispatch(orderLineItem, dispatches);
                } else {
                    console.warn('Order line item not found for dispatch', data.orderLineItemId);
                }
            }

            const dispatch = {
                id: emptyGuid,
                isActive: true,
                createdOn: new Date(),
                orderLineItemId: data.orderLineItemId,
                driverId: data.driverId,
                brokerId: data.brokerId,
                equipmentId: data.equipmentId ?? undefined,
                startDate: saveDates.startDate,
                endDate: saveDates.endDate,
                onsiteTime: data.onsiteTime,
                yardTime: data.yardTime,
                clientId: emptyGuid,
                released: data.released,
                haulToAddressLine1: data.haulToAddressLine1,
                haulToAddressLine2: data.haulToAddressLine2,
                haulToCity: data.haulToCity,
                haulToState: data.haulToState,
                haulToZipCode: data.haulToZipCode,
                haulToCountry: data.haulToCountry,
                haulToDirections: data.haulToDirections,
                haulFromAddressLine1: data.haulFromAddressLine1,
                haulFromAddressLine2: data.haulFromAddressLine2,
                haulFromCity: data.haulFromCity,
                haulFromState: data.haulFromState,
                haulFromZipCode: data.haulFromZipCode,
                haulFromCountry: data.haulFromCountry,
                haulFromDirections: data.haulFromDirections,
                haulToSiteName: data.haulToSiteName,
                haulToRegion: data.haulToRegion,
                haulToRegionId: data.haulToRegionId,
                haulFromSiteName: data.haulFromSiteName,
                haulFromRegion: data.haulFromRegion,
                haulFromRegionId: data.haulFromRegionId,
                driverComment: data.driverComment,
                isHaulFromAddressLocked: data.isHaulFromAddressLocked,
                isHaulToAddressLocked: data.isHaulToAddressLocked,
                isOrderInReview: data.isOrderInReview,
                haulQuantity: haulQuantity ?? undefined,
            } as DispatchDto;

            if (data.id !== emptyGuid) {
                dispatch.id = data.id;
                dispatch.isActive = data.isActive;
                dispatch.createdOn = data.createdOn;
                dispatch.clientId = data.clientId;
            }

            return dispatch;
        },
        [date, schedulerRef, orderLineItems, calculateHourlyHaulQuantity, dispatches]
    );

    const handleSave = useCallback(
        (data: any, originalData: any, record: DispatchEventModel) => {
            if (

                data.id === emptyGuid ||
                !areDateTimesEqual(data.startDate, originalData.startDate) ||
                !areDateTimesEqual(data.endDate, originalData.endDate) ||
                !areDateTimesEqual(data.onsiteTime, originalData.onsiteTime) ||
                !areDateTimesEqual(data.yardTime, originalData.yardTime) ||
                data.orderLineItemId !== originalData.orderLineItemId ||
                (data.driverId ?? null) !== (originalData.driverId ?? null) ||
                (data.brokerId ?? null) !== (originalData.brokerId ?? null) ||
                data.equipmentId !== originalData.equipmentId ||
                data.released !== originalData.released ||
                data.haulToAddressLine1 !== originalData.haulToAddressLine1 ||
                data.haulToAddressLine2 !== originalData.haulToAddressLine2 ||
                data.haulToCity !== originalData.haulToCity ||
                data.haulToState !== originalData.haulToState ||
                data.haulToZipCode !== originalData.haulToZipCode ||
                data.haulToCountry !== originalData.haulToCountry ||
                data.haulToDirections !== originalData.haulToDirections ||
                data.haulFromAddressLine1 !== originalData.haulFromAddressLine1 ||
                data.haulFromAddressLine2 !== originalData.haulFromAddressLine2 ||
                data.haulFromCity !== originalData.haulFromCity ||
                data.haulFromState !== originalData.haulFromState ||
                data.haulFromZipCode !== originalData.haulFromZipCode ||
                data.haulFromCountry !== originalData.haulFromCountry ||
                data.haulFromDirections !== originalData.haulFromDirections ||
                data.haulToSiteName !== originalData.haulToSiteName ||
                data.haulToRegionId !== originalData.haulToRegionId ||
                data.haulFromSiteName !== originalData.haulFromSiteName ||
                data.haulFromRegionId !== originalData.haulFromRegionId ||
                data.isHaulFromAddressLocked !== originalData.isHaulFromAddressLocked ||
                data.isHaulToAddressLocked !== originalData.isHaulToAddressLocked ||
                data.resourceId !== originalData.resourceId ||
                data.driverComment !== originalData.driverComment ||
                data.haulQuantity !== originalData.haulQuantity
            ) {
                const dto = createDispatchDto(data);
                if (dto.brokerId || dto.driverId) {
                    if (!dto.isOrderInReview) {
                        schedulerRef?.current?.instance?.eventStore?.remove(record);

                        if (data.id === emptyGuid && (pendingSaveCount.current > 0 || loadingData)) {
                            Toast.show('Please wait for the previous dispatch to finish saving before adding another.');
                            return;
                        }

                        if (data.id === emptyGuid) {
                            pendingSaveCount.current++;
                        }
                        updateDispatch(dto).finally(() => {
                            if (data.id === emptyGuid) {
                                pendingSaveCount.current--;
                            }
                        });
                    }
                }
            }
        },
        [createDispatchDto, updateDispatch, schedulerRef, loadingData]
    );

    const onEventStoreUpdate = useCallback(
        ({ record, changes }: { source: any; record: DispatchEventModel; changes: any }): void => {
            const newData = (record as any).data;
            const originalData = (record as any).originalData;
            if (changes.updatedOn) {
                return;
            }
            var resource = haulerManagementData?.resources.find((x) => x.id === record.resourceId);
            if (!resource) {
                return;
            }

            if (newData.resourceId !== originalData.resourceId) {
                if (resource.haulerType === 'Drivers') {
                    newData.brokerId = null;
                    newData.driverId = record.resourceId;
                    newData.equipmentId = resource?.equipmentId ?? null;
                } else {
                    newData.brokerId = record.resourceId;
                    newData.driverId = null;
                    newData.equipmentId = null;
                }
            }
            handleSave(newData, originalData, record);
        },
        [haulerManagementData, handleSave]
    );

    const calculateRemainingHaulQuantity = useCallback(
        (orderLineItemId: string, excludeDispatchId?: string) => {
            const lineItem = orderLineItems.find((r: any) => r.id === orderLineItemId);
            if (!lineItem) return undefined;

            const loadQuantity = lineItem.loadOrQuantityReq;
            if (loadQuantity === undefined || lineItem.orderBy === OrderByType.Equipment) return undefined;

            if (lineItem.lineItemType === LineItemTypes.Hourly && lineItem.orderBy === OrderByType.Quantity) return loadQuantity;

            const otherDispatches = dispatches.filter(
                (d: any) => d.orderLineItemId === orderLineItemId &&
                    d.id !== excludeDispatchId
            );
            const usedQuantity = otherDispatches.reduce((sum: number, d: any) => {
                return sum + d.haulQuantity;
            }, 0);

            return Math.max(loadQuantity - usedQuantity, 0);
        },
        [orderLineItems, dispatches]
    );

    return (
        <>
            {isEditEventOpen && (
                <EventEditor
                    initValues={selectedEvent}
                    isHaulerView
                    orderLineItemDto={orderLineItems?.find((r: OrderLineItemDto) => r.id === selectedEvent?.getData('orderLineItemId'))}
                    handleDelete={() => {
                        if (selectedEvent) {
                            const eventStore = schedulerRef.current.instance.eventStore;
                            eventStore.remove(selectedEvent);
                            deleteDispatch({ id: selectedEvent.getData('id') });
                            setEditEventOpen(false);
                            setSelectedEvent(undefined);
                        }
                    }}
                    open={isEditEventOpen}
                    cancel={() => {
                        setEditEventOpen(false);
                        setSelectedEvent(undefined);
                    }}
                    save={() => {
                        setEditEventOpen(false);
                        setSelectedEvent(undefined);
                    }}
                    disabled={disabled}
                    calculateRemainingHaulQuantity={calculateRemainingHaulQuantity}
                />
            )}
            <div id='main'>
                <HaulerManagementDispatchingViewScheduler
                    schedulerRef={schedulerRef}
                    config={{ ...schedulerConfig }}
                    resources={haulerManagementData?.resources ?? []}
                    events={haulerManagementData?.events ?? []}
                    crudManager={crudManager}
                    onEventStoreUpdate={onEventStoreUpdate}
                />
                <BryntumSplitter />
                <div id='unplannedContainer'>
                    <OrderLineItemGrid schedulerRef={schedulerRef} dataList={haulerManagementData?.orderLineItems ?? []} disabled={disabled} date={date} />
                </div>
            </div>
        </>
    );
};
